import { useState, useEffect, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { updateType, postNewTypeEnum } from './adminPageRequest';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Tooltip,
  Zoom,
} from '@mui/material';
import { typesImportantForAnalytics } from '../../shared/constants/constants';
import { IssueAndServiceSharedValues } from '../../shared/constants/IssueAndServiceKeys';

function AddEnumTypesModal({ open, setOpen, existingItems, editMode, setEditMode, selectedItem }) {
  // Component constants
  const typeNameCharLimit = 70;
  const typeCategoryCharLimit = 15;
  const typeBackgroundColorOptions = ['Red', 'Green', 'None'];
  const typeProactivityOptions = ['Proactive', 'Reactive'];
  const typePermissionsOptions = useMemo(() => {
    return ['User', 'Manager', 'Admin'];
  }, []); //will only run once

  const [typeName, setTypeName] = useState('');
  const [typeCategory, setTypeCategory] = useState('');
  const [typeBackgroundColor, setTypeBackgroundColor] = useState('');
  const [typeProactivity, setTypeProactivity] = useState('');
  const [typePermissions, setTypePermissions] = useState([...typePermissionsOptions]);
  const [typeNameInputStatus, setTypeNameInputStatus] = useState({ error: false, message: '' });
  const [typeCategoryInputStatus, setTypeCategoryInputStatus] = useState({
    error: false,
    message: '',
  });
  const [typeBackgroundColorInputStatus, setTypeBackgroundColorInputStatus] = useState({
    error: false,
    message: '',
  });
  const [typeProactivityInputStatus, setTypeProactivityInputStatus] = useState({ error: false, message: '' });
  const dispatch = useDispatch();

  useEffect(() => {
    if (editMode) {
      setTypeName(selectedItem?.name || '');
      setTypeCategory(selectedItem?.type || '');
      setTypeBackgroundColor(selectedItem?.backgroundColor || '');
      setTypeProactivity(selectedItem?.proactivity || '');
      setTypePermissions(selectedItem?.permissions || [...typePermissionsOptions]);
    } else {
      setTypeName('');
      setTypeCategory('');
      setTypeBackgroundColor('');
      setTypeProactivity('');
      setTypePermissions([...typePermissionsOptions]);
    }
  }, [editMode, selectedItem, typePermissionsOptions]);

  const checkEmptyFieldsAndCharacterLength = () => {
    let pass = true;

    if (!typeName) {
      setTypeNameInputStatus({ error: true, message: '' });
      pass = false;
    } else {
      setTypeNameInputStatus({ error: false, message: '' });
    }

    if (!typeCategory) {
      setTypeCategoryInputStatus({ error: true, message: '' });
      pass = false;
    } else {
      setTypeCategoryInputStatus({ error: false, message: '' });
    }

    if (!typeBackgroundColor) {
      setTypeBackgroundColorInputStatus({ error: true, message: '' });
      pass = false;
    } else {
      setTypeBackgroundColorInputStatus({ error: false, message: '' });
    }

    if (!typeProactivity) {
      setTypeProactivityInputStatus({ error: true, message: '' });
      pass = false;
    } else {
      setTypeProactivityInputStatus({ error: false, message: '' });
    }

    return pass;
  };

  const checkDuplicates = () => {
    let numOfDuplicates = 0;
    if (editMode) {
      existingItems.forEach((item) => {
        if (item.name.toLowerCase() === typeName.toLowerCase() && item._id !== selectedItem._id) {
          numOfDuplicates += 1;
        }
      });
    } else {
      existingItems.forEach((item) => {
        if (item.name.toLowerCase() === typeName.toLowerCase()) {
          numOfDuplicates += 1;
        }
      });
    }

    if (numOfDuplicates > 0) {
      dispatch(
        openSnackbar({
          severity: 'warning',
          message: `${numOfDuplicates} type(s) with the same name already exists. Please check if you are trying to add a type that already exists.`,
        })
      );
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!checkEmptyFieldsAndCharacterLength()) return;

    if (!checkDuplicates()) return;

    if (editMode) {
      await updateType(selectedItem._id, {
        typeName: typeName,
        typeCategory: typeCategory,
        typeBackgroundColor: typeBackgroundColor,
        typeProactivity: typeProactivity,
        typePermissions: typePermissions,
      })
        .then((res) => {
          setOpen(false);
          setEditMode(false);
          dispatch(openSnackbar({ message: `Type updated successfully`, severity: 'success' }));
        })
        .catch((err) => {
          console.log(err.message);
          dispatch(openSnackbar({ message: `Failed to update type`, severity: 'error' }));
        });
    } else {
      await postNewTypeEnum({
        typeName: typeName,
        typeCategory: typeCategory,
        typeBackgroundColor: typeBackgroundColor,
        typeProactivity: typeProactivity,
        typePermissions: typePermissions,
      })
        .then((res) => {
          setOpen(false);
          dispatch(openSnackbar({ message: `Type added successfully`, severity: 'success' }));
        })
        .catch((err) => {
          console.log(err.message);
          dispatch(openSnackbar({ message: `Failed to add type`, severity: 'error' }));
        });
    }
  };

  const queryClient = useQueryClient();
  const itemMutation = useMutation({
    mutationFn: (e) => onSubmit(e),
    onSuccess: () => {
      queryClient.invalidateQueries(['types', 'adminPage']);
      queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        setEditMode(false);
        setOpen(false);
      }}
    >
      <Card
        sx={{
          p: '30px',
          width: '30%',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: '9999',
          justifyContent: 'center',
          textAlign: 'left',
          alignItems: 'flex-start',
        }}
      >
        <Typography align="left" variant="h5">
          {editMode ? `Edit Type` : `Add Type`}
        </Typography>
        <form>
          <Box
            sx={{
              pt: '2vh',
              pb: '1vh',
              borderTop: 'solid gray 1px',
              mt: '2vh',
              mb: '1vh',
            }}
          >
            <Tooltip
              title={<Typography variant={'body2'}>{`Name of Type`}</Typography>}
              TransitionComponent={Zoom}
              enterDelay={200}
              placement={'right'}
            >
              <TextField
                disabled={typesImportantForAnalytics.includes(typeName) && editMode}
                required
                value={typeName}
                onChange={(e) => {
                  if (e.target.value.length > typeNameCharLimit) {
                    setTypeName(e.target.value.slice(0, typeNameCharLimit));
                  } else {
                    setTypeName(e.target.value);
                  }
                }}
                sx={{ width: '100%' }}
                id={'add-modal-type-name-input'}
                label={IssueAndServiceSharedValues.type}
                variant="outlined"
                error={typeNameInputStatus.error}
                helperText={
                  <Box>
                    {typeNameInputStatus.message ? `${typeNameInputStatus.message}` : ''}
                    <Box
                      sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                    >{`${typeName.length}/${typeNameCharLimit} character(s)`}</Box>
                  </Box>
                }
              ></TextField>
            </Tooltip>
          </Box>
          <Box sx={{ pt: '1vh', pb: '1vh', mt: '1vh', mb: '1vh' }}>
            <Tooltip
              title={<Typography variant={'body2'}>{`Category of Type`}</Typography>}
              TransitionComponent={Zoom}
              enterDelay={200}
              placement={'right'}
            >
              <TextField
                required
                value={typeCategory}
                onChange={(e) => {
                  if (e.target.value.length > typeCategoryCharLimit) {
                    setTypeCategory(e.target.value.slice(0, typeCategoryCharLimit));
                  } else {
                    setTypeCategory(e.target.value);
                  }
                }}
                sx={{ width: '100%' }}
                id={'add-modal-type-category-input'}
                label={'Category'}
                variant="outlined"
                helperText={
                  <Box>
                    {typeCategoryInputStatus.message ? `${typeCategoryInputStatus.message}` : ''}
                    <Box
                      sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                    >{`${typeCategory.length}/${typeCategoryCharLimit} character(s)`}</Box>
                  </Box>
                }
                error={typeCategoryInputStatus.error}
              ></TextField>
            </Tooltip>
          </Box>
          <Box sx={{ pt: '1vh', pb: '1vh', mt: '1vh', mb: '1vh' }}>
            <Autocomplete
              disablePortal
              clearOnEscape
              openOnFocus
              autoHighlight
              autoSelect
              id={'add-modal-type-backgroundColor-input'}
              options={typeBackgroundColorOptions}
              value={typeBackgroundColor === '' ? null : typeBackgroundColor}
              renderInput={(params) => (
                <Tooltip
                  title={
                    <Typography variant={'body2'}>
                      {
                        'Background color of the Media Interaction/Service Log table cells that show this type. Note that the topmost type will have priority in determining the type cell color in Media Interaction/Service Log tables.'
                      }
                    </Typography>
                  }
                  TransitionComponent={Zoom}
                  enterDelay={200}
                  placement={'right'}
                >
                  <TextField
                    required
                    {...params}
                    sx={{ width: '100%' }}
                    variant="outlined"
                    label={'Table Cell Background Color'}
                    error={typeBackgroundColorInputStatus.error}
                    helperText={typeBackgroundColorInputStatus.message}
                  />
                </Tooltip>
              )}
              onChange={(e, value) => {
                setTypeBackgroundColor(value);
              }}
              className={''}
            />
          </Box>
          <Box sx={{ pt: '1vh', pb: '1vh', mt: '1vh', mb: '1vh' }}>
            <Autocomplete
              disablePortal
              clearOnEscape
              openOnFocus
              autoHighlight
              autoSelect
              id={'add-modal-type-proactivity-input'}
              options={typeProactivityOptions}
              value={typeProactivity === '' ? null : typeProactivity}
              renderInput={(params) => (
                <Tooltip
                  title={
                    <Typography variant={'body2'}>{'Whether Type is created proactively or reactively'}</Typography>
                  }
                  TransitionComponent={Zoom}
                  enterDelay={200}
                  placement={'right'}
                >
                  <TextField
                    {...params}
                    required
                    sx={{ width: '100%' }}
                    variant="outlined"
                    label={'Proactivity'}
                    error={typeProactivityInputStatus.error}
                    helperText={typeProactivityInputStatus.message}
                  />
                </Tooltip>
              )}
              onChange={(e, value) => {
                setTypeProactivity(value);
              }}
              className={''}
            />
          </Box>
          <Box
            sx={{
              pt: '1vh',
              pb: '2vh',
              borderBottom: 'solid gray 1px',
              mt: '1vh',
              mb: '2vh',
            }}
          >
            <FormControl variant="outlined">
              <Tooltip
                title={
                  <Typography
                    variant={'body2'}
                  >{`Determines the permission level of users required to see services with this type as an entry to the Service Log table. However, issues with this type in the Media Interactions table will not be affected. Note that for a user with a given permission to see a service in the Service Log table, ALL types contained in said service must have the given permission.`}</Typography>
                }
                TransitionComponent={Zoom}
                enterDelay={200}
                placement={'right'}
              >
                <FormLabel>Service Log Entry Availability Permissions</FormLabel>
              </Tooltip>
              <FormGroup>
                {typePermissionsOptions.map((permissionOption) => (
                  <FormControlLabel
                    control={<Checkbox checked={typePermissions.includes(permissionOption)} />}
                    value={permissionOption}
                    label={permissionOption}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newTypePermissions = [...typePermissions];
                        newTypePermissions.push(permissionOption);
                        setTypePermissions(newTypePermissions);
                      } else {
                        const newTypePermissions = typePermissions.filter((permission) => {
                          return permission !== permissionOption;
                        });
                        setTypePermissions(newTypePermissions);
                      }
                    }}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '1vw', mt: '3vh' }}>
            <Button
              variant="contained"
              sx={{ width: '6vw', height: '5vh', color: 'white', backgroundColor: 'red' }}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              sx={{ width: '6vw', height: '5vh' }}
              onClick={(e) => itemMutation.mutate(e)}
              variant="contained"
              color="primary"
            >
              {editMode ? 'Edit' : 'Add'}
            </Button>
          </Box>
        </form>
      </Card>
    </Modal>
  );
}

export default AddEnumTypesModal;
